import React from "react";
import { FormSection } from "@gelsenwasser/react";
import { useFormikContext } from "formik";
import { Antrag, DefenderDokumentStatus, DefenderUploadedDokument, emptyDefenderUploadedDokument } from "interfaces/Application";
import { Col, Row } from "react-bootstrap";
import { useDokumentStatusQueries } from "domain/applications/applicationsQueries";

interface SummaryLabelProps {
  label: string;
  value: string | JSX.Element;
}
const SummaryLabel: React.FC<SummaryLabelProps> = ({ label, value }) => {
  return (
    <>
      <Col md={4}>
        <strong>{label}</strong>
      </Col>
      <Col md={8}>{value}</Col>
    </>
  );
};

const checkSummaryFileStatus = (
  duds: DefenderUploadedDokument[] | undefined,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
  values: Antrag
) => {
  if (duds) {
    const betriebsHaftplichtDateiStatus = duds.find(
      (dud) => dud.documentId === values.betriebsHaftplichtDatei?.documentId
    )?.status;
    if (
      betriebsHaftplichtDateiStatus !== DefenderDokumentStatus.NotChecked &&
      values.betriebsHaftplichtDatei?.status === DefenderDokumentStatus.NotChecked
    ) {
      setFieldValue("betriebsHaftplichtDatei", {
        ...values.betriebsHaftplichtDatei,
        status: betriebsHaftplichtDateiStatus,
      });
    }
    const gewerbeAnmeldungDateiStatus = duds.find(
      (dud) => dud.documentId === values.gewerbeAnmeldungDatei?.documentId
    )?.status;
    if (
      gewerbeAnmeldungDateiStatus !== DefenderDokumentStatus.NotChecked &&
      values.gewerbeAnmeldungDatei?.status === DefenderDokumentStatus.NotChecked
    ) {
      setFieldValue("gewerbeAnmeldungDatei", {
        ...values.gewerbeAnmeldungDatei,
        status: gewerbeAnmeldungDateiStatus,
      });
    }
    const handwerksrollenEintragungDateiStatus = duds.find(
      (dud) => dud.documentId === values.handwerksrollenEintragungDatei?.documentId
    )?.status;
    if (
      handwerksrollenEintragungDateiStatus !== DefenderDokumentStatus.NotChecked &&
      values.handwerksrollenEintragungDatei?.status === DefenderDokumentStatus.NotChecked
    ) {
      setFieldValue("handwerksrollenEintragungDatei", {
        ...values.handwerksrollenEintragungDatei,
        status: handwerksrollenEintragungDateiStatus,
      });
    }
  }
};

const DocumentsSummary: React.VFC = () => {
  const { values, setFieldValue } = useFormikContext<Antrag>();
  const { data: duds } = useDokumentStatusQueries(
    [
      values.betriebsHaftplichtDatei ?? emptyDefenderUploadedDokument,
      values.gewerbeAnmeldungDatei ?? emptyDefenderUploadedDokument,
      values.handwerksrollenEintragungDatei ?? emptyDefenderUploadedDokument,
    ],
    values.installationsUnternehmenId
  );

  React.useEffect(() => {
    checkSummaryFileStatus(duds, setFieldValue, values);
  }, [duds?.map((d) => d.documentId + d.status).join(",")]);

  return (
    <FormSection title="Dokumente">
      <Row>
        <SummaryLabel
          label="Betriebshaftpflicht"
          value={
            typeof values.betriebsHaftplichtDatei === "object"
              ? `${values.betriebsHaftplichtDatei.dateiName} - Status: ${values.betriebsHaftplichtDatei.status}`
              : ""
          }
        />
        <SummaryLabel
          label="Gewerbeanmeldung"
          value={
            typeof values.gewerbeAnmeldungDatei === "object"
              ? `${values.gewerbeAnmeldungDatei.dateiName} - Status: ${values.gewerbeAnmeldungDatei.status}`
              : ""
          }
        />
        <SummaryLabel
          label="Handwerksrolleneintragung"
          value={
            typeof values.handwerksrollenEintragungDatei === "object"
              ? `${values.handwerksrollenEintragungDatei.dateiName} - Status: ${values.handwerksrollenEintragungDatei.status}`
              : ""
          }
        />
      </Row>
    </FormSection>
  );
};

export default DocumentsSummary;
